import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth, hasPermission } from '@/contexts/AuthContext'
import { supabase } from '@/lib/supabase'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/useToast'
import { Mail, Shield, Loader2, UserPlus, Trash2 } from 'lucide-react'
import { useAdminInvites, useRevokeInvite } from '@/hooks/useAdminInvites'
import { formatDate } from '@/lib/utils'

type Role = 'super_admin' | 'admin' | 'viewer' | 'investor'

export function InvitesPage() {
  const { adminUser } = useAuth()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<Role>('viewer')
  const [isLoading, setIsLoading] = useState(false)

  const canInvite = hasPermission(adminUser?.role, 'super_admin')

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!adminUser) return

    setIsLoading(true)

    try {
      // 1. Send the auth invite email via the edge function.
      const { data: fnData, error: fnError } = await supabase.functions.invoke('invite-admin', {
        body: { email, role, invited_by: adminUser.id },
      })

      if (fnError) throw fnError
      if (fnData?.error) throw new Error(fnData.error)

      // 2. Record the invite in admin_invites so it shows up in history.
      // The edge function was not writing this row, which is why the history
      // table was stale. RLS policy "Super admins can manage invites" allows
      // this insert from a super_admin session.
      const { error: insertError } = await supabase
        .from('admin_invites')
        .insert({
          email,
          role,
          invited_by: adminUser.id,
        })

      if (insertError) throw insertError

      toast({
        title: 'Invite sent',
        description: `Invite sent to ${email}`,
      })

      setEmail('')
      setRole('viewer')
      await queryClient.invalidateQueries({ queryKey: ['admin-invites'] })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to send invite'
      toast({
        title: 'Invite failed',
        description: message,
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  if (!canInvite) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <h2 className="text-xl sm:text-2xl font-bold">Invites</h2>
        <Card>
          <CardContent className="py-8 sm:py-8 text-center text-muted-foreground p-4 sm:p-6">
            <Shield className="h-10 w-10 sm:h-12 sm:w-12 mx-auto mb-4 opacity-50" />
            <p className="text-sm sm:text-base">Only Super Admins can invite new users.</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <h2 className="text-xl sm:text-2xl font-bold">Invite Admin</h2>

      <Card>
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <UserPlus className="h-5 w-5" />
            Send Invitation
          </CardTitle>
          <CardDescription className="text-sm">
            Invite a new team member to the admin dashboard
          </CardDescription>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0">
          <form onSubmit={handleInvite} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm">Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="colleague@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="role" className="text-sm">Role</Label>
              <select
                id="role"
                value={role}
                onChange={(e) => setRole(e.target.value as Role)}
                className="w-full h-11 sm:h-10 px-3 rounded-md border border-input bg-background text-base sm:text-sm"
              >
                <option value="viewer">Viewer (Read-only)</option>
                <option value="admin">Admin (Manage users and tokens)</option>
                <option value="super_admin">Super Admin (Full access)</option>
                <option value="investor">Investor (Read-only dashboard)</option>
              </select>
            </div>

            <Button
              type="submit"
              className="w-full bg-dextoro text-black hover:bg-dextoro/90 mt-6 sm:mt-4"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                'Send Invite'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      <InviteHistory />
    </div>
  )
}

function InviteHistory() {
  const { data: invites, isLoading } = useAdminInvites()
  const revokeInvite = useRevokeInvite()
  const { toast } = useToast()

  const handleRevoke = async (id: string, email: string) => {
    if (!window.confirm(`Revoke invite for ${email}?`)) return
    try {
      await revokeInvite.mutateAsync(id)
      toast({ title: 'Invite revoked', description: `Invite for ${email} has been revoked.` })
    } catch {
      toast({ title: 'Error', description: 'Failed to revoke invite.', variant: 'destructive' })
    }
  }

  const statusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-amber-500/20 text-amber-400">Pending</span>
      case 'expired':
        return <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-muted text-muted-foreground">Expired</span>
      case 'accepted':
        return <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-emerald-500/20 text-emerald-400">Accepted</span>
      default:
        return null
    }
  }

  return (
    <Card>
      <CardHeader className="p-4 sm:p-6">
        <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
          <Mail className="h-5 w-5" />
          Invite History
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0 sm:p-0">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : !invites || invites.length === 0 ? (
          <p className="px-4 py-6 text-center text-sm text-muted-foreground">No invites sent yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="border-b border-border">
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Email</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Role</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Sent</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Status</th>
                <th className="px-4 py-3 text-right font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invites.map((invite) => (
                <tr key={invite.id} className="border-b border-border last:border-0 hover:bg-muted/50">
                  <td className="px-4 py-3 font-medium">{invite.email}</td>
                  <td className="px-4 py-3 capitalize">{invite.role.replace('_', ' ')}</td>
                  <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{formatDate(invite.createdAt)}</td>
                  <td className="px-4 py-3">{statusBadge(invite.status)}</td>
                  <td className="px-4 py-3 text-right">
                    {invite.status === 'pending' && (
                      <button
                        onClick={() => handleRevoke(invite.id, invite.email)}
                        className="rounded p-1.5 text-red-400 hover:bg-red-500/10"
                        title="Revoke invite"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
